import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import MobilePagination from '../../rider/parcel/mobile.pagination';
import API from '../../../create/api';
import querystring from 'querystring';
import moment from 'moment';
import PickupRequest from '../dashboard/pickupRequest';
import FiltersSection from './FiltersSection';

const PICKUP_STATUS_OPTIONS = [
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'RIDER_ASSIGNED', label: 'Rider Assigned' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELED_BY_MERCHANT', label: 'Canceled by Merchant' },
];

export default function PickupRequestsPage() {
  const location = useLocation();
  const [pickupRequests, setPickupRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    date: '',
    status: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState({ _sort: 'id:desc' });

  function buildQuery() {
    const query: any = {
      _start: (page - 1) * limit,
      _limit: limit,
    };
    if (filters.date) {
      query.date = moment(filters.date).format('YYYY-MM-DD');
    }
    if (filters.status) {
      query.status = filters.status;
    }
    if (sort._sort) {
      query._sort = sort._sort;
    }
    return querystring.encode(query);
  }

  async function fetchAPI() {
    setLoading(true);
    const qs = buildQuery();

    try {
      const [requests, count] = await Promise.all([
        API.getAll(`${process.env.REACT_APP_API_SERVER}/pickup-requests?${qs}`),
        API.count(`${process.env.REACT_APP_API_SERVER}/pickup-requests/count?${qs}`),
      ]);
      setPickupRequests(requests as any[]);
      setTotalCount(Number(count));
    } catch (error) {}
    setLoading(false);
  }

  async function pickup_request_update(id: number, data: object) {
    try {
      await API.update(`${process.env.REACT_APP_API_SERVER}/pickup-requests`, id, data);

      window.location.href = `/dashboard/pickup-requests`;
    } catch (error) {
      console.error('Error updating pickup request:', error);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status) {
      setFilters((prevFilters) => ({ ...prevFilters, status }));
    }
  }, [location.search]); // Run effect when URL changes

  useEffect(() => {
    fetchAPI();
    // eslint-disable-next-line
  }, [filters, page, limit, sort]);

  const handlePrint = () => {
    window.print();
  };

  const handleSortChange = (field: string) => {
    setSort({
      _sort: field,
    });
  };

  const allSelected = useMemo(
    () => pickupRequests.length > 0 && pickupRequests.every((req: any) => req.selected),
    [pickupRequests],
  );

  // For mobile view, a simple row rendering
  const MobileRow = ({ row, index }: { row: any; index: number }) => (
    <Box key={row.id} p={2} mb={2} border="1px solid #ddd" borderRadius={4} bgcolor="#fff">
      <Typography variant="h6" gutterBottom>
        Pickup Request #{row.id}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Date:</strong> {row.date}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Status:</strong> {PICKUP_STATUS_OPTIONS.find((s) => s.value === row.status)?.label}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Company:</strong> {row.company?.name || 'N/A'}
      </Typography>
      {row.status !== 'DONE' && (
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => pickup_request_update(row.id, { status: 'DONE' })}
          >
            Mark as Done
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Grid container spacing={2}>
      {/* Pickup Request and Filters Section */}
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          p={2}
          bgcolor="white"
          borderRadius={4}
          boxShadow={1}
        >
          <Grid item xs={12} mb={{ xs: 2, sm: 0 }}>
            {/* Pickup Request Component */}
            <Box flex="1">
              <PickupRequest />
            </Box>
          </Grid>

          <Grid item xs={12}>
            {/* Filters Section */}
            <Box flex="1">
              <FiltersSection filters={filters} setFilters={setFilters} loading={loading} fetchAPI={fetchAPI} />
            </Box>
          </Grid>
        </Box>
      </Grid>

      {/* Desktop Table */}
      <Grid item xs={12} className="hidden sm:block">
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)} // Update sort handler
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ backgroundColor: 'lightgray' }}>
              <TableRow>
                <TableCell onClick={() => handleSortChange('id')}>
                  <b>ID</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('date')}>
                  <b>Date</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('status')}>
                  <b>Status</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('action')}>
                  <b>Action</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pickupRequests?.length !== 0 ? (
                pickupRequests.map((row: any, idx: number) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{PICKUP_STATUS_OPTIONS.find((s) => s.value === row.status)?.label}</TableCell>
                    <TableCell>
                      {row.status !== 'DONE' && row.status !== 'CANCELED_BY_MERCHANT' && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => pickup_request_update(row.id, { status: 'CANCELED_BY_MERCHANT' })}
                        >
                          Mark as Canceled
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No pickup requests found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Mobile View */}
      <Grid item xs={12} className="block sm:hidden">
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)}
        />
        {pickupRequests.map((row: any, i: number) => (
          <MobileRow row={row} index={i + 1} key={row.id} />
        ))}
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)}
        />
      </Grid>
    </Grid>
  );
}
